import { DateFilter as DateFilterType } from '@chiroup/core/types/AdminMetrics.type';
import * as Tabs from '@radix-ui/react-tabs';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, Monitor, TrendingUp, Users } from 'lucide-react';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { cn } from '../../lib/utils';
import AcquisitionFunnel from './components/AcquisitionFunnel';
import AnalyticsLoading from './components/AnalyticsLoading';
import DashboardNavigation from './components/DashboardNavigation';
import ExecutiveOverview from './components/ExecutiveOverview';
import MetricCard from './components/MetricCard';
import analyticsService from './services/analytics.service';

import {
  CHURN_METRICS,
  HEALTH_METRICS,
  NPS_METRICS,
  SECTION_METRICS,
  SUPPORT_METRICS,
} from '../../constants/metrics.constants';

// Use centralized metric constants to eliminate magic strings

const AnalyticsDashboard = () => {
  const { dashboardId } = useParams<{ dashboardId: string }>();

  const [dateFilter, setDateFilter] = useState<DateFilterType>({
    type: 'monthToDate',
  });

  const [activeSection, setActiveSection] = useState<string>('acquisition');
  const [minimumDurationComplete, setMinimumDurationComplete] = useState(false);

  const { data, isLoading, error, refetch, isFetching } = useQuery(
    ['analytics-metrics', dashboardId, dateFilter],
    () => analyticsService.getMetrics(dashboardId!, dateFilter),
    {
      enabled: !!dashboardId,
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );

  // Show full loading screen for initial load - only hide when both data is loaded AND minimum duration is complete
  const shouldShowLoading = isLoading || !minimumDurationComplete;

  if (shouldShowLoading) {
    return <AnalyticsLoading onMinimumDurationComplete={() => setMinimumDurationComplete(true)} />;
  }

  // Show loading overlay for filter changes (but not for initial load)
  const showLoadingOverlay = isFetching && !isLoading;

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <DashboardNavigation currentDashboardId={dashboardId || 'general'} />
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mt-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Unable to load dashboard metrics
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  {error instanceof Error ? error.message : 'An error occurred'}
                </div>
                <div className="mt-3">
                  <button
                    onClick={() => refetch()}
                    className="bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm font-medium"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const allMetrics = data?.metrics || [];

  // Filter metrics based on active section
  const getSectionMetrics = (section: string): string[] => {
    switch (section) {
      case 'acquisition':
        return SECTION_METRICS.acquisition as unknown as string[];
      case 'essentials':
        return SECTION_METRICS.essentials as unknown as string[];
      case 'ehr':
        return SECTION_METRICS.ehr as unknown as string[];
      default:
        return [];
    }
  };

  const filteredMetrics = allMetrics.filter(metric =>
    getSectionMetrics(activeSection).includes(metric.id)
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 relative">
      <DashboardNavigation currentDashboardId={dashboardId || 'general'} />

      {/* Loading Screen for Filter Changes */}
      {showLoadingOverlay && <AnalyticsLoading />}

      <div className="p-4">
        {/* Executive Overview - Hero Section */}
        <ExecutiveOverview
          metrics={allMetrics}
          dateFilter={dateFilter}
          onDateFilterChange={setDateFilter}
        />

        {/* Enhanced Section Tabs */}
        <Tabs.Root value={activeSection} onValueChange={setActiveSection} className="mb-6 mt-4">
          <Tabs.List className="grid w-full grid-cols-1 sm:grid-cols-3 gap-3 p-2 bg-gradient-to-r from-slate-50/50 to-gray-50/80 backdrop-blur-sm rounded-3xl border border-white/20 shadow-2xl">
            <Tabs.Trigger
              value="acquisition"
              className={cn(
                'group relative flex items-center justify-center px-6 py-4 rounded-2xl font-semibold text-sm transition-all duration-500 ease-in-out transform',
                'data-[state=active]:bg-gradient-to-br data-[state=active]:from-blue-500 data-[state=active]:via-indigo-500 data-[state=active]:to-purple-600',
                'data-[state=active]:text-white data-[state=active]:shadow-2xl data-[state=active]:scale-105',
                'data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80',
                'data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl',
                'focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-transparent'
              )}
            >
              <motion.div
                className="flex items-center space-x-3"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <div
                  className={cn(
                    'p-2 rounded-xl transition-all duration-300',
                    activeSection === 'acquisition' ? 'bg-white/20 shadow-lg' : 'bg-blue-100'
                  )}
                >
                  <TrendingUp
                    className={cn(
                      'w-5 h-5 transition-colors duration-300',
                      activeSection === 'acquisition' ? 'text-white' : 'text-blue-600'
                    )}
                  />
                </div>
                <span className="font-bold tracking-wide">Acquisition Funnel</span>
              </motion.div>
            </Tabs.Trigger>

            <Tabs.Trigger
              value="essentials"
              className={cn(
                'group relative flex items-center justify-center px-6 py-4 rounded-2xl font-semibold text-sm transition-all duration-500 ease-in-out transform',
                'data-[state=active]:bg-gradient-to-br data-[state=active]:from-emerald-500 data-[state=active]:via-green-500 data-[state=active]:to-teal-600',
                'data-[state=active]:text-white data-[state=active]:shadow-2xl data-[state=active]:scale-105',
                'data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80',
                'data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl',
                'focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2 focus:ring-offset-transparent'
              )}
            >
              <motion.div
                className="flex items-center space-x-3"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <div
                  className={cn(
                    'p-2 rounded-xl transition-all duration-300',
                    activeSection === 'essentials' ? 'bg-white/20 shadow-lg' : 'bg-green-100'
                  )}
                >
                  <Users
                    className={cn(
                      'w-5 h-5 transition-colors duration-300',
                      activeSection === 'essentials' ? 'text-white' : 'text-green-600'
                    )}
                  />
                </div>
                <span className="font-bold tracking-wide">Essentials Metrics</span>
              </motion.div>
            </Tabs.Trigger>

            <Tabs.Trigger
              value="ehr"
              className={cn(
                'group relative flex items-center justify-center px-6 py-4 rounded-2xl font-semibold text-sm transition-all duration-500 ease-in-out transform',
                'data-[state=active]:bg-gradient-to-br data-[state=active]:from-violet-500 data-[state=active]:via-purple-500 data-[state=active]:to-indigo-600',
                'data-[state=active]:text-white data-[state=active]:shadow-2xl data-[state=active]:scale-105',
                'data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80',
                'data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl',
                'focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 focus:ring-offset-transparent'
              )}
            >
              <motion.div
                className="flex items-center space-x-3"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <div
                  className={cn(
                    'p-2 rounded-xl transition-all duration-300',
                    activeSection === 'ehr' ? 'bg-white/20 shadow-lg' : 'bg-purple-100'
                  )}
                >
                  <Monitor
                    className={cn(
                      'w-5 h-5 transition-colors duration-300',
                      activeSection === 'ehr' ? 'text-white' : 'text-purple-600'
                    )}
                  />
                </div>
                <span className="font-bold tracking-wide">EHR Metrics</span>
              </motion.div>
            </Tabs.Trigger>
          </Tabs.List>
        </Tabs.Root>

        {/* Enhanced Content Area with Animations */}
        <div className="relative min-h-[600px]">
          <AnimatePresence mode="wait">
            {activeSection === 'acquisition' && (
              <motion.div
                key="acquisition"
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="absolute inset-0"
              >
                <div className="bg-gradient-to-br from-blue-50/50 via-indigo-50/30 to-purple-50/50 rounded-3xl p-8 shadow-2xl border border-blue-100/50 backdrop-blur-sm">
                  <AcquisitionFunnel metrics={allMetrics} />
                </div>
              </motion.div>
            )}

            {activeSection === 'essentials' && (
              <motion.div
                key="essentials"
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="absolute inset-0"
              >
                <div className="bg-gradient-to-br from-emerald-50/50 via-green-50/30 to-teal-50/50 rounded-3xl p-8 shadow-2xl border border-green-100/50 backdrop-blur-sm">
                  {/* Custom Essentials Layout */}
                  {/* Row 1: Churn Metrics */}
                  <motion.div
                    className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.4 }}
                  >
                    {filteredMetrics
                      .filter(metric =>
                        [CHURN_METRICS.ESSENTIALS_RATE, CHURN_METRICS.ESSENTIALS_COUNT].includes(
                          metric.id as any
                        )
                      )
                      .map((metric, index) => (
                        <motion.div
                          key={metric.id}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.2 + index * 0.1, duration: 0.3 }}
                        >
                          <MetricCard metric={metric} />
                        </motion.div>
                      ))}
                  </motion.div>

                  {/* Row 2: Health Score Donut Charts */}
                  <motion.div
                    className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.4 }}
                  >
                    {filteredMetrics
                      .filter(metric =>
                        [
                          HEALTH_METRICS.ESSENTIALS_POST_BOARDING,
                          HEALTH_METRICS.ESSENTIALS_ONBOARDING,
                        ].includes(metric.id as any)
                      )
                      .sort((a, b) => {
                        // Ensure onboarding comes first (left), post-boarding second (right)
                        if (a.id === HEALTH_METRICS.ESSENTIALS_ONBOARDING) return -1;
                        if (b.id === HEALTH_METRICS.ESSENTIALS_ONBOARDING) return 1;

                        return 0;
                      })
                      .map((metric, index) => (
                        <motion.div
                          key={metric.id}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.4 + index * 0.1, duration: 0.3 }}
                        >
                          <MetricCard metric={metric} />
                        </motion.div>
                      ))}
                  </motion.div>

                  {/* Row 3: NPS Chart - Full Width */}
                  <motion.div
                    className="mb-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.4 }}
                  >
                    <div className="w-full">
                      {filteredMetrics
                        .filter(metric =>
                          [NPS_METRICS.ESSENTIALS_NPS_SCORE].includes(metric.id as any)
                        )
                        .map(metric => (
                          <motion.div
                            key={metric.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.4, duration: 0.3 }}
                          >
                            <MetricCard metric={metric} />
                          </motion.div>
                        ))}
                    </div>
                  </motion.div>

                  {/* Row 4: Support Metrics */}
                  <motion.div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.4 }}
                  >
                    {filteredMetrics
                      .filter(metric =>
                        [
                          SUPPORT_METRICS.ESSENTIALS_TICKETS,
                          SUPPORT_METRICS.ESSENTIALS_FIRST_RESPONSE,
                          SUPPORT_METRICS.ESSENTIALS_RESOLUTION,
                        ].includes(metric.id as any)
                      )
                      .map((metric, index) => (
                        <motion.div
                          key={metric.id}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.7 + index * 0.1, duration: 0.3 }}
                        >
                          <MetricCard metric={metric} />
                        </motion.div>
                      ))}
                  </motion.div>

                  {filteredMetrics.length === 0 && !isLoading && (
                    <motion.div
                      className="text-center py-12"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2, duration: 0.4 }}
                    >
                      <div className="text-gray-400 text-6xl mb-4">
                        <Users className="w-16 h-16 mx-auto" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        No metrics available for this section
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Metrics will appear here once data is available for the selected section and
                        time period.
                      </p>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            )}

            {activeSection === 'ehr' && (
              <motion.div
                key="ehr"
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="absolute inset-0"
              >
                <div className="bg-gradient-to-br from-violet-50/50 via-purple-50/30 to-indigo-50/50 rounded-3xl p-8 shadow-2xl border border-purple-100/50 backdrop-blur-sm">
                  {/* Custom EHR Layout */}
                  {/* Row 1: Churn Metrics Only */}
                  <motion.div
                    className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.4 }}
                  >
                    {filteredMetrics
                      .filter(metric =>
                        [CHURN_METRICS.EHR_COUNT, CHURN_METRICS.EHR_RATE].includes(metric.id as any)
                      )
                      .map((metric, index) => (
                        <motion.div
                          key={metric.id}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.2 + index * 0.1, duration: 0.3 }}
                        >
                          <MetricCard metric={metric} />
                        </motion.div>
                      ))}
                  </motion.div>

                  {/* Row 2: Product Churn Metrics (includes logo churn + downgrades) */}
                  <motion.div
                    className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.4 }}
                  >
                    {filteredMetrics
                      .filter(metric =>
                        [
                          CHURN_METRICS.EHR_DOWNGRADE_COUNT,
                          CHURN_METRICS.EHR_DOWNGRADE_RATE,
                        ].includes(metric.id as any)
                      )
                      .map((metric, index) => {
                        // Add logo churn to product churn counts
                        const logoChurnMetric = filteredMetrics.find(
                          m => m.id === CHURN_METRICS.EHR_COUNT
                        );
                        const logoChurnValue =
                          typeof logoChurnMetric?.value === 'number' ? logoChurnMetric.value : 0;

                        const adjustedMetric = {
                          ...metric,
                          value:
                            metric.id === CHURN_METRICS.EHR_DOWNGRADE_COUNT &&
                            typeof metric.value === 'number'
                              ? metric.value + logoChurnValue
                              : metric.value,
                        };

                        return (
                          <motion.div
                            key={metric.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.4 + index * 0.1, duration: 0.3 }}
                          >
                            <MetricCard metric={adjustedMetric} />
                          </motion.div>
                        );
                      })}
                  </motion.div>

                  {/* Row 3: Health Score Donut Chart */}
                  <motion.div
                    className="flex justify-center mb-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.4 }}
                  >
                    <div className="w-full max-w-md">
                      {filteredMetrics
                        .filter(metric => [HEALTH_METRICS.EHR_SCORE].includes(metric.id as any))
                        .map(metric => (
                          <motion.div
                            key={metric.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.4, duration: 0.3 }}
                          >
                            <MetricCard metric={metric} />
                          </motion.div>
                        ))}
                    </div>
                  </motion.div>

                  {/* Row 4: NPS Chart - Full Width */}
                  <motion.div
                    className="mb-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.4 }}
                  >
                    <div className="w-full">
                      {filteredMetrics
                        .filter(metric => [NPS_METRICS.EHR_NPS_SCORE].includes(metric.id as any))
                        .map(metric => (
                          <motion.div
                            key={metric.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.5, duration: 0.3 }}
                          >
                            <MetricCard metric={metric} />
                          </motion.div>
                        ))}
                    </div>
                  </motion.div>

                  {/* Row 5: Support Metrics */}
                  <motion.div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7, duration: 0.4 }}
                  >
                    {filteredMetrics
                      .filter(metric =>
                        [
                          SUPPORT_METRICS.EHR_TICKETS,
                          SUPPORT_METRICS.EHR_FIRST_RESPONSE,
                          SUPPORT_METRICS.EHR_RESOLUTION,
                        ].includes(metric.id as any)
                      )
                      .map((metric, index) => (
                        <motion.div
                          key={metric.id}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.8 + index * 0.1, duration: 0.3 }}
                        >
                          <MetricCard metric={metric} />
                        </motion.div>
                      ))}
                  </motion.div>

                  {filteredMetrics.length === 0 && !isLoading && (
                    <motion.div
                      className="text-center py-12"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2, duration: 0.4 }}
                    >
                      <div className="text-gray-400 text-6xl mb-4">
                        <Monitor className="w-16 h-16 mx-auto" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        No metrics available for this section
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Metrics will appear here once data is available for the selected section and
                        time period.
                      </p>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Loading State */}
        {isLoading && (
          <motion.div
            className="fixed inset-0 bg-black/10 backdrop-blur-sm z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl p-8 shadow-2xl border border-gray-100 flex items-center space-x-4"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
            >
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <span className="text-lg font-medium text-gray-900">Loading analytics...</span>
            </motion.div>
          </motion.div>
        )}

        {/* Default fallback for other sections */}
        {activeSection !== 'acquisition' &&
          activeSection !== 'essentials' &&
          activeSection !== 'ehr' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredMetrics.map(metric => (
                  <MetricCard key={metric.id} metric={metric} />
                ))}
              </div>

              {filteredMetrics.length === 0 && !isLoading && (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-6xl mb-4">
                    <Users className="w-16 h-16 mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No metrics available for this section
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Metrics will appear here once data is available for the selected section and
                    time period.
                  </p>
                </div>
              )}
            </>
          )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
